import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { db } from '../../lib/supabase';
import { DEFAULT_ZOOM_URL } from '../../data/lmsData';

/**
 * ZoomSettings
 * -----------------------------------------------------------------------
 * Lightweight settings view that lets authorized users (admins, professors,
 * and clerical staff) update the default Zoom meeting URL used for all
 * live lectures that don't have a custom per-item URL configured.
 *
 * This view only edits the `default_zoom_url` row in the `settings` table
 * — it never exposes any course-module or master-module controls. It is
 * safe for the Clerical role.
 * -----------------------------------------------------------------------
 */
const ZoomSettings = () => {
  const { currentUser } = useLMS();

  const [defaultZoomUrl, setDefaultZoomUrl] = useState(DEFAULT_ZOOM_URL);
  const [zoomUrlInput, setZoomUrlInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await db.getSetting('default_zoom_url');
        const value = data?.value || DEFAULT_ZOOM_URL;
        setDefaultZoomUrl(value);
        setZoomUrlInput(value);
      } catch (err) {
        console.log('Using fallback Zoom URL');
        setZoomUrlInput(DEFAULT_ZOOM_URL);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);

    const trimmed = (zoomUrlInput || '').trim();
    if (!trimmed) {
      setError('Zoom URL is required.');
      return;
    }
    // Basic validation — must look like a URL
    try {
      new URL(trimmed);
    } catch (_) {
      setError('Please enter a valid URL (including https://).');
      return;
    }

    setSaving(true);
    try {
      const { error: upsertError } = await db.upsertSetting(
        'default_zoom_url',
        trimmed,
        'Default Zoom meeting URL for all live lectures',
        currentUser?.id
      );
      if (upsertError) throw upsertError;
      setDefaultZoomUrl(trimmed);
      setSaved(true);
      // Auto-dismiss success message after a few seconds
      setTimeout(() => setSaved(false), 3500);
    } catch (err) {
      console.error('Error saving Zoom URL:', err);
      setError(err.message || 'Failed to save Zoom URL.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        Loading Zoom settings…
      </div>
    );
  }

  return (
    <div className="zoom-settings-view" style={{ padding: '24px', maxWidth: '780px', margin: '0 auto' }}>
      <div className="page-header">
        <div>
          <h1>🎥 Zoom Settings</h1>
          <p>Update the default Zoom meeting URL used for live lectures.</p>
        </div>
      </div>

      <div
        style={{
          background: 'var(--card-bg, #fff)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          padding: '24px',
          marginTop: '16px'
        }}
      >
        <div className="info-box" style={{ marginBottom: '20px' }}>
          <span className="info-icon">ℹ️</span>
          <p style={{ margin: 0 }}>
            This URL is used for every live lecture that doesn't have its own custom URL.
            Updating it here will immediately apply to all applicable lectures across all cohorts.
            It does not change any course content, modules, or schedules.
          </p>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>Current default</div>
          <a
            href={defaultZoomUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ wordBreak: 'break-all' }}
          >
            {defaultZoomUrl}
          </a>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-group">
            <label>Default Zoom Meeting URL</label>
            <input
              type="url"
              value={zoomUrlInput}
              onChange={(e) => setZoomUrlInput(e.target.value)}
              placeholder="https://zoom.us/j/..."
              disabled={saving}
              required
            />
          </div>

          {error && (
            <p className="error-message" style={{ color: '#dc2626', marginTop: '8px' }}>
              ❌ {error}
            </p>
          )}
          {saved && (
            <p className="success-message" style={{ color: '#059669', marginTop: '8px' }}>
              ✅ Default Zoom URL updated.
            </p>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || !zoomUrlInput.trim() || zoomUrlInput.trim() === defaultZoomUrl}
            >
              {saving ? 'Saving…' : 'Save Zoom URL'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setZoomUrlInput(defaultZoomUrl)}
              disabled={saving}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ZoomSettings;
